import { useQuery } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { regionalReportingApis } from 'common/api'
import { useAppSelector } from 'common/hooks/redux'

const fetchPersonalizationLiveVariants = async ({
  accountId,
  personalizationId,
  personalizationRegion,
}: {
  accountId: string
  personalizationId: string
  personalizationRegion: string
}) => {
  if (!personalizationRegion) {
    throw new Error('Region is not defined')
  }

  if (!accountId) {
    throw new Error('Account id is not defined for this content')
  }

  if (!personalizationId) {
    throw new Error('Personalization id is not defined')
  }

  if (!regionalReportingApis) {
    throw new Error('Regional reporting APIs are not configured')
  }

  const regionKey = personalizationRegion.toLowerCase()
  const regionalApi = regionalReportingApis[regionKey]

  if (!regionalApi) {
    throw new Error(
      `Regional API not found for region: ${regionKey}. Available regions: ${Object.keys(
        regionalReportingApis
      ).join(', ')}`
    )
  }

  const result = await regionalApi.post(
    `/accounts/${accountId}/personalization/${personalizationId}/results/variants`,
    {
      mature_only: 'false',
    }
  )

  return result.data
}

type PersonalizationLiveVariantResult = {
  variantId: string
  variantText: string
  variantStatus: string
  clickRate: string
  clicks: number
  opens: number
  sends: number
  openRate: string
  clickUplift: string
  machineOpens: number
  openRateExcludedMachineOpens: string
  openUpliftMachineOpensExcluded: string
  openUplift: string
  isHoldout?: boolean
  sentiments?: any
  combinationId?: string
}

type PersonalizationLiveVariantsResponse = {
  items: PersonalizationLiveVariantResult[]
}

export const useGetPersonalizationLiveVariantsQuery = ({
  personalizationId,
  personalizationRegion,
  enabled = true,
}: {
  personalizationId: string | undefined
  personalizationRegion: string | undefined
  enabled?: boolean
}) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const flags = useFlags()

  const isEnabled =
    enabled && !!accountId && !!personalizationId && !!personalizationRegion

  return useQuery({
    queryKey: [
      'personalization-live-variants',
      accountId,
      personalizationId,
      personalizationRegion,
    ],

    queryFn: async () => {
      const response = await fetchPersonalizationLiveVariants({
        accountId,
        personalizationId: personalizationId!,
        ...(flags.switchToRegionalizedData && personalizationRegion
          ? { personalizationRegion: personalizationRegion.toLowerCase() }
          : { personalizationRegion: 'eu' }),
      })

      return response.items.filter(
        (item: PersonalizationLiveVariantResult) =>
          item.variantStatus === 'live' || item.variantStatus === 'approved'
      )
    },

    enabled: isEnabled,
    refetchOnWindowFocus: false,

    // 30 seconds
    staleTime: 30000,
  })
}
